import React from 'react';
import { PageSEO } from "@/components/PageSEO";
import { Dealer } from "@/types/dealer";
import { CityStats } from "@/services/seo/cityStatsService";
import { 
  createDealersListingSchema, 
  createBreadcrumbsSchema, 
  createServiceSchema,
  createFAQSchema
} from "@/utils/schemaUtils";

interface DealersCitySEOProps {
  formattedCity: string;
  citySlug: string;
  dealers: Dealer[];
  cityStats?: CityStats;
}

export function DealersCitySEO({ 
  formattedCity, 
  citySlug, 
  dealers,
  cityStats
}: DealersCitySEOProps) {
  // Generate enhanced title with better SEO optimization
  const generateTitle = () => {
    if (!cityStats || cityStats.totalDealers === 0) {
      return `Compro Oro a ${formattedCity} - Trova i Migliori Negozi | Quotazioni Oro in Tempo Reale - Gildy`;
    }
    
    const { totalDealers, averageRating, verifiedDealers } = cityStats;
    
    if (averageRating > 0) {
      return `Compro Oro ${formattedCity}: ${totalDealers} Negozi, Rating ${averageRating.toFixed(1)}★ | Quotazioni Oro - Gildy`;
    } else {
      return `Compro Oro ${formattedCity}: ${totalDealers} Negozi Verificati | Migliori Prezzi Oro - Gildy`;
    }
  };

  const generateDescription = () => {
    if (!cityStats || cityStats.totalDealers === 0) {
      return `🥇 Trova i migliori compro oro a ${formattedCity}. Confronta prezzi oro e argento, leggi recensioni verificate e ottieni quotazioni personalizzate in tempo reale. Servizio gratuito e sicuro.`;
    }
    
    const { totalDealers, averageRating, verifiedDealers, averageSpread, topRatedDealer } = cityStats;
    
    let description = `🥇 Scopri ${totalDealers} compro oro a ${formattedCity}`;
    
    if (verifiedDealers > 0) {
      description += `, ${verifiedDealers} negozi verificati`;
    }
    
    if (averageRating > 0) {
      description += `, rating medio ${averageRating.toFixed(1)}★`;
    }
    
    if (averageSpread > 0) {
      description += `, spread medio ${averageSpread.toFixed(1)}%`;
    }
    
    if (topRatedDealer) {
      description += `. Miglior negozio: ${topRatedDealer.name} (${topRatedDealer.rating}★)`;
    }
    
    description += '. 💰 Confronta prezzi, leggi recensioni e ottieni quotazioni oro in tempo reale. Servizio gratuito.';
    
    return description;
  };

  // Create dealers listing schema with the current city
  const dealersListingSchema = createDealersListingSchema(dealers, formattedCity);
  
  // Create breadcrumbs schema
  const breadcrumbsSchema = createBreadcrumbsSchema([
    { name: 'Home', url: '/' },
    { name: 'Compro Oro', url: '/compro-oro' },
    { name: formattedCity, url: `/compro-oro/${citySlug}` }
  ]);
  
  // Create enhanced service schema with stats
  const serviceSchema = createServiceSchema({
    name: `Compro Oro a ${formattedCity}`,
    description: generateDescription(),
    url: `https://www.gildy.it/compro-oro/${citySlug}`,
    provider: {
      name: "Gildy",
      url: "https://www.gildy.it"
    },
    areaServed: formattedCity,
    // Add aggregateRating if we have stats
    ...(cityStats && cityStats.averageRating > 0 && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: cityStats.averageRating,
        reviewCount: cityStats.totalDealers,
        bestRating: 5,
        worstRating: 1
      }
    })
  });

  // Create FAQ schema using the utility function with city-specific questions
  const faqQuestions = [
    {
      question: `Come trovare i migliori compro oro a ${formattedCity}?`,
      answer: `Per trovare i migliori compro oro a ${formattedCity}, utilizza la nostra piattaforma per confrontare prezzi, leggere recensioni verificate e visualizzare le valutazioni dei clienti. Tutti i negozi sono verificati per garantire affidabilità.`
    },
    {
      question: `Quanti compro oro ci sono a ${formattedCity}?`,
      answer: `Attualmente abbiamo ${cityStats?.totalDealers || 'diversi'} compro oro verificati a ${formattedCity}${cityStats?.verifiedDealers ? `, di cui ${cityStats.verifiedDealers} completamente verificati` : ''}. Ogni negozio è controllato per garantire trasparenza nelle transazioni.`
    },
    {
      question: `Quali sono i prezzi dell'oro a ${formattedCity}?`,
      answer: `I prezzi dell'oro a ${formattedCity} sono basati sulle quotazioni internazionali in tempo reale. Ogni negozio mostra il proprio spread in modo trasparente, permettendoti di confrontare facilmente le offerte migliori.`
    },
    {
      question: `È sicuro vendere oro a ${formattedCity}?`,
      answer: `Sì, tutti i compro oro a ${formattedCity} nella nostra piattaforma sono verificati e seguono rigorosi standard di sicurezza. Offriamo anche supporto clienti per garantire transazioni sicure e trasparenti.`
    }
  ];

  const faqSchema = createFAQSchema(faqQuestions);
  
  // Enhanced keywords with stats-based variations and local SEO
  const baseKeywords = [
    `compro oro ${formattedCity}`, 
    `negozi oro ${formattedCity}`, 
    `valutazione oro ${formattedCity}`, 
    `vendere oro ${formattedCity}`, 
    `quotazione oro ${formattedCity}`,
    `migliori compro oro ${formattedCity}`, 
    `compro oro vicino a me`,
    "quotazione oro tempo reale",
    "valutazione gioielli",
    "vendita oro usato",
    "oro 18 carati",
    "oro 24 carati", 
    "argento 925",
    "gioielli usati"
  ];

  // Add stats-based keywords
  const statsKeywords = [];
  if (cityStats && cityStats.totalDealers > 0) {
    if (cityStats.verifiedDealers > 0) {
      statsKeywords.push(
        `compro oro verificati ${formattedCity}`,
        `negozi oro certificati ${formattedCity}`,
        `compro oro sicuri ${formattedCity}`
      );
    }
    if (cityStats.averageRating > 4) {
      statsKeywords.push(
        `migliori compro oro ${formattedCity}`, 
        `compro oro recensioni ${formattedCity}`,
        `compro oro top rated ${formattedCity}`
      );
    }
    if (cityStats.topRatedDealer) {
      statsKeywords.push(`${cityStats.topRatedDealer.name} ${formattedCity}`);
    }
    if (cityStats.averageSpread && cityStats.averageSpread < 10) {
      statsKeywords.push(
        `compro oro spread basso ${formattedCity}`,
        `migliori prezzi oro ${formattedCity}`
      );
    }
  }

  // Add local variations and nearby areas keywords
  const localKeywords = [
    `compro oro centro ${formattedCity}`,
    `negozi oro zona ${formattedCity}`,
    `vendere oro ${formattedCity} centro`,
    `quotazioni oro ${formattedCity} oggi`,
    `prezzo oro ${formattedCity}`,
    `valutazione gioielli ${formattedCity}`
  ];

  const keywords = [...baseKeywords, ...statsKeywords, ...localKeywords];
  
  return (
    <PageSEO 
      title={generateTitle()}
      description={generateDescription()}
      canonicalPath={`/compro-oro/${citySlug}`}
      structuredData={[dealersListingSchema, breadcrumbsSchema, serviceSchema, faqSchema]}
      keywords={keywords}
    />
  );
}
